import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { 
  VehicleData, 
  ConsultationProduct, 
  ConsultaPlacaBasicaResponse, 
  ConsultationProductType, 
  SolicitarRelatorioResponse, 
  TipoConsultaRelatorio,
  ConsultarProtocoloResponse,
  SituacaoConsulta,
  ConsultarRenainfResponse,
  ConsultarPrecoFipeResponse,
  ConsultarProprietarioResponse,
  ConsultarLeilaoPrimeResponse,
  ConsultarSinistroPTResponse,
} from '../types';

// Define callable functions
const consultPlateFn = httpsCallable(functions, 'consultPlate');
const getConsultationTypesFn = httpsCallable(functions, 'getConsultationTypes');
const getConsultationHistoryFn = httpsCallable(functions, 'getConsultationHistory');
const getConsultationProductsFn = httpsCallable(functions, 'getConsultationProducts');
const consultaPlacaBasicaFn = httpsCallable(functions, 'consultaPlacaBasica');
const consultaChassiBasicaFn = httpsCallable(functions, 'consultaChassiBasica');
const solicitarRelatorioFn = httpsCallable(functions, 'solicitarRelatorioEndpoint');
const consultarProtocoloFn = httpsCallable(functions, 'consultarProtocoloEndpoint');
const consultaRenainfFn = httpsCallable(functions, 'consultaRenainfEndpoint');
const consultaPrecoFipeFn = httpsCallable(functions, 'consultaPrecoFipeEndpoint');
const consultaProprietarioFn = httpsCallable(functions, 'consultaProprietarioEndpoint');
const consultaLeilaoPrimeFn = httpsCallable(functions, 'consultaLeilaoPrimeEndpoint');
const consultaSinistroPTFn = httpsCallable(functions, 'consultaSinistroPTEndpoint');

/**
 * Result from consultation
 */
export interface ConsultationResult {
  vehicleData: VehicleData;
  creditsUsed: number;
  remainingCredits: number;
}

/**
 * Consultation type from API
 */
export interface ConsultationType {
  id: string;
  name: string;
  creditCost: number;
  apiLevel: 'basic' | 'advanced' | 'premium';
  description: string;
}

/**
 * Historical consultation record
 */
export interface ConsultationRecord {
  id: string;
  userId: string;
  plate?: string;
  placa?: string;
  consultationType: string;
  creditsUsed: number;
  result: VehicleData | unknown;
  createdAt: { _seconds: number };
  apiSource: string;
  // Relatório specific fields
  protocolo?: string;
  tipoConsulta?: TipoConsultaRelatorio;
  situacao?: SituacaoConsulta;
  urlPdf?: string;
}

/**
 * Consult a license plate
 */
export async function consultLicensePlate(
  plate: string,
  consultationType: string
): Promise<ConsultationResult> {
  try {
    const result = await consultPlateFn({ 
      plate: plate.toUpperCase().replace(/[^A-Z0-9]/g, ''),
      consultationType 
    });
    
    return result.data as ConsultationResult;
  } catch (error: unknown) {
    const err = error as { code?: string; message?: string };
    
    // Handle specific error codes
    if (err.code === 'functions/failed-precondition') {
      throw new Error('Saldo insuficiente. Por favor, compre mais créditos.');
    }
    if (err.code === 'functions/invalid-argument') {
      throw new Error(err.message || 'Dados inválidos. Verifique a placa.');
    }
    if (err.code === 'functions/unauthenticated') {
      throw new Error('Sessão expirada. Por favor, faça login novamente.');
    }
    
    throw new Error('Erro ao consultar placa. Tente novamente.');
  }
}

/**
 * Get available consultation types
 */
export async function getConsultationTypes(): Promise<ConsultationType[]> {
  try {
    const result = await getConsultationTypesFn();
    return result.data as ConsultationType[];
  } catch (error) {
    console.error('Error fetching consultation types:', error);
    
    // Return default types if API fails
    return [
      {
        id: 'basic',
        name: 'Consulta Básica',
        creditCost: 1,
        apiLevel: 'basic',
        description: 'Dados básicos do veículo',
      },
      {
        id: 'complete',
        name: 'Consulta Completa',
        creditCost: 3,
        apiLevel: 'advanced',
        description: 'Dados completos com histórico',
      },
    ];
  }
}

/**
 * Get user's consultation history
 */
export async function getConsultationHistory(
  limit: number = 20
): Promise<ConsultationRecord[]> {
  try {
    const result = await getConsultationHistoryFn({ limit });
    return result.data as ConsultationRecord[];
  } catch (error) {
    console.error('Error fetching consultation history:', error);
    return [];
  }
}

/**
 * Map consultation types to product format for UI
 */
export function mapTypesToProducts(types: ConsultationType[]): ConsultationProduct[] {
  const featuresByLevel: Record<string, string[]> = {
    basic: ['Marca/Modelo/Cor', 'Status Roubo/Furto', 'Ano Fabricação'],
    advanced: ['Tudo da Básica', 'Débitos e Multas', 'Restrições Judiciais', 'Histórico de Leilão'],
    premium: ['Tudo da Completa', 'Relatório Multi-fonte', 'Score de Confiança', 'Histórico de Proprietários'],
  };

  return types.map((type) => ({
    id: type.id,
    title: type.name,
    description: type.description,
    price: type.creditCost,
    features: featuresByLevel[type.apiLevel] || featuresByLevel.basic,
    apiLevel: type.apiLevel,
  }));
}

// =====================================================
// PRODUCT-BASED CONSULTATIONS
// =====================================================

/**
 * Get available consultation products from Firestore
 * Os produtos são retornados ordenados pelo campo 'order' e filtrados por 'active'
 */
export async function getConsultationProductsList(): Promise<ConsultationProductType[]> {
  try {
    const result = await getConsultationProductsFn();
    return result.data as ConsultationProductType[];
  } catch (error) {
    console.error('Error fetching consultation products:', error);
    // Retorna array vazio em caso de erro - o usuário verá que não há produtos disponíveis
    return [];
  }
}

/**
 * Consulta Placa - Informações Básicas
 */
export async function consultaPlacaBasica(placa: string): Promise<ConsultaPlacaBasicaResponse> {
  try {
    const result = await consultaPlacaBasicaFn({ 
      placa: placa.toUpperCase().replace(/[^A-Z0-9]/g, '')
    });
    
    return result.data as ConsultaPlacaBasicaResponse;
  } catch (error: unknown) {
    const err = error as { code?: string; message?: string };
    
    // Handle specific error codes
    if (err.code === 'functions/failed-precondition') {
      throw new Error('Saldo insuficiente. Por favor, compre mais créditos.');
    }
    if (err.code === 'functions/invalid-argument') {
      throw new Error(err.message || 'Dados inválidos. Verifique a placa.');
    }
    if (err.code === 'functions/unauthenticated') {
      throw new Error('Sessão expirada. Por favor, faça login novamente.');
    }
    if (err.code === 'functions/internal') {
      throw new Error(err.message || 'Erro ao consultar placa. Tente novamente.');
    }
    
    throw new Error('Erro ao consultar placa. Tente novamente.');
  }
}

/**
 * Consulta Chassi - Informações Básicas
 */
export async function consultaChassiBasica(chassi: string): Promise<ConsultaPlacaBasicaResponse> {
  try {
    const result = await consultaChassiBasicaFn({ 
      chassi: chassi.toUpperCase().replace(/[^A-Z0-9]/g, '')
    });
    
    return result.data as ConsultaPlacaBasicaResponse;
  } catch (error: unknown) {
    const err = error as { code?: string; message?: string };
    
    // Handle specific error codes
    if (err.code === 'functions/failed-precondition') {
      throw new Error('Saldo insuficiente. Por favor, compre mais créditos.');
    }
    if (err.code === 'functions/invalid-argument') {
      throw new Error(err.message || 'Dados inválidos. Verifique o chassi.');
    }
    if (err.code === 'functions/unauthenticated') {
      throw new Error('Sessão expirada. Por favor, faça login novamente.');
    }
    if (err.code === 'functions/internal') {
      throw new Error(err.message || 'Erro ao consultar chassi. Tente novamente.');
    }
    
    throw new Error('Erro ao consultar chassi. Tente novamente.');
  }
}

/**
 * Solicitar Relatório / Histórico
 */
export async function solicitarRelatorio(
  placa: string,
  tipoConsulta: TipoConsultaRelatorio,
  informacoesAdicionais?: string[]
): Promise<SolicitarRelatorioResponse> {
  try {
    const result = await solicitarRelatorioFn({ 
      placa: placa.toUpperCase().replace(/[^A-Z0-9]/g, ''),
      tipoConsulta,
      informacoesAdicionais,
    });
    
    return result.data as SolicitarRelatorioResponse;
  } catch (error: unknown) {
    const err = error as { code?: string; message?: string };
    
    // Handle specific error codes
    if (err.code === 'functions/failed-precondition') {
      throw new Error('Saldo insuficiente. Por favor, compre mais créditos.');
    }
    if (err.code === 'functions/invalid-argument') {
      throw new Error(err.message || 'Dados inválidos. Verifique os campos.');
    }
    if (err.code === 'functions/unauthenticated') {
      throw new Error('Sessão expirada. Por favor, faça login novamente.');
    }
    if (err.code === 'functions/internal') {
      throw new Error(err.message || 'Erro ao solicitar relatório. Tente novamente.');
    }
    
    throw new Error('Erro ao solicitar relatório. Tente novamente.');
  }
}

/**
 * Consultar Protocolo - Verificar status de um relatório
 * FREE - não custa créditos (já foi pago na solicitação)
 */
export async function consultarProtocolo(
  protocolo: string,
  tipoRetorno: 'PDF' | 'JSON' = 'PDF'
): Promise<ConsultarProtocoloResponse> {
  try {
    const result = await consultarProtocoloFn({ 
      protocolo,
      tipoRetorno,
    });
    
    return result.data as ConsultarProtocoloResponse;
  } catch (error: unknown) {
    const err = error as { code?: string; message?: string };
    
    // Handle specific error codes
    if (err.code === 'functions/permission-denied') {
      throw new Error('Protocolo não encontrado ou não pertence a este usuário.');
    }
    if (err.code === 'functions/invalid-argument') {
      throw new Error(err.message || 'Protocolo inválido.');
    }
    if (err.code === 'functions/unauthenticated') {
      throw new Error('Sessão expirada. Por favor, faça login novamente.');
    }
    if (err.code === 'functions/internal') {
      throw new Error(err.message || 'Erro ao consultar protocolo. Tente novamente.');
    }
    
    throw new Error('Erro ao consultar protocolo. Tente novamente.');
  }
}

/**
 * Get reports (relatórios) from consultation history
 * Filter by consultation type 'solicitar_relatorio'
 */
export async function getReportHistory(limit: number = 20): Promise<ConsultationRecord[]> {
  try {
    const allHistory = await getConsultationHistory(limit * 2); // Get more to filter
    return allHistory.filter(record => record.consultationType === 'solicitar_relatorio');
  } catch (error) {
    console.error('Error fetching report history:', error);
    return [];
  }
}

/**
 * Consulta RENAINF - Débitos por Infrações de Trânsito
 */
export async function consultaRenainf(placa: string): Promise<ConsultarRenainfResponse> {
  try {
    const result = await consultaRenainfFn({ 
      placa: placa.toUpperCase().replace(/[^A-Z0-9]/g, '')
    });
    
    return result.data as ConsultarRenainfResponse;
  } catch (error: unknown) {
    const err = error as { code?: string; message?: string };
    
    // Handle specific error codes
    if (err.code === 'functions/failed-precondition') {
      throw new Error('Saldo insuficiente. Por favor, compre mais créditos.');
    }
    if (err.code === 'functions/invalid-argument') {
      throw new Error(err.message || 'Dados inválidos. Verifique a placa.');
    }
    if (err.code === 'functions/unauthenticated') {
      throw new Error('Sessão expirada. Por favor, faça login novamente.');
    }
    if (err.code === 'functions/internal') {
      throw new Error(err.message || 'Erro ao consultar RENAINF. Tente novamente.');
    }
    
    throw new Error('Erro ao consultar infrações. Tente novamente.');
  }
}

/**
 * Consulta Preço FIPE - Valor atual e histórico de 12 meses
 */
export async function consultaPrecoFipe(placa: string): Promise<ConsultarPrecoFipeResponse> {
  try {
    const result = await consultaPrecoFipeFn({ 
      placa: placa.toUpperCase().replace(/[^A-Z0-9]/g, '')
    });
    
    return result.data as ConsultarPrecoFipeResponse;
  } catch (error: unknown) {
    const err = error as { code?: string; message?: string };
    
    // Handle specific error codes
    if (err.code === 'functions/failed-precondition') {
      throw new Error('Saldo insuficiente. Por favor, compre mais créditos.');
    }
    if (err.code === 'functions/invalid-argument') {
      throw new Error(err.message || 'Dados inválidos. Verifique a placa.');
    }
    if (err.code === 'functions/unauthenticated') {
      throw new Error('Sessão expirada. Por favor, faça login novamente.');
    }
    if (err.code === 'functions/internal') {
      throw new Error(err.message || 'Erro ao consultar Preço FIPE. Tente novamente.');
    }
    
    throw new Error('Erro ao consultar Preço FIPE. Tente novamente.');
  }
}

/**
 * Consulta Proprietário Atual
 * Returns partial owner information (first name and masked document)
 */
export async function consultaProprietario(placa: string): Promise<ConsultarProprietarioResponse> {
  try {
    const result = await consultaProprietarioFn({ 
      placa: placa.toUpperCase().replace(/[^A-Z0-9]/g, '')
    });
    
    return result.data as ConsultarProprietarioResponse;
  } catch (error: unknown) {
    const err = error as { code?: string; message?: string };
    
    // Handle specific error codes
    if (err.code === 'functions/failed-precondition') {
      throw new Error('Saldo insuficiente. Por favor, compre mais créditos.');
    }
    if (err.code === 'functions/invalid-argument') {
      throw new Error(err.message || 'Dados inválidos. Verifique a placa.');
    }
    if (err.code === 'functions/unauthenticated') {
      throw new Error('Sessão expirada. Por favor, faça login novamente.');
    }
    if (err.code === 'functions/internal') {
      throw new Error(err.message || 'Erro ao consultar proprietário. Tente novamente.');
    }
    
    throw new Error('Erro ao consultar proprietário. Tente novamente.');
  }
}

/**
 * Consulta Leilão Prime - Registro de ofertas em leilões com análise IA
 * NOTE: This query may take longer (up to 5 minutes) due to image processing
 */
export async function consultaLeilaoPrime(placa: string): Promise<ConsultarLeilaoPrimeResponse> {
  try {
    const result = await consultaLeilaoPrimeFn({ 
      placa: placa.toUpperCase().replace(/[^A-Z0-9]/g, '')
    });
    
    return result.data as ConsultarLeilaoPrimeResponse;
  } catch (error: unknown) {
    const err = error as { code?: string; message?: string };
    
    // Handle specific error codes
    if (err.code === 'functions/failed-precondition') {
      throw new Error('Saldo insuficiente. Por favor, compre mais créditos.');
    }
    if (err.code === 'functions/invalid-argument') {
      throw new Error(err.message || 'Dados inválidos. Verifique a placa.');
    }
    if (err.code === 'functions/unauthenticated') {
      throw new Error('Sessão expirada. Por favor, faça login novamente.');
    }
    if (err.code === 'functions/internal') {
      throw new Error(err.message || 'Erro ao consultar leilão. Tente novamente.');
    }
    
    throw new Error('Erro ao consultar leilão. Tente novamente.');
  }
}

/**
 * Consulta Sinistro com Perda Total
 */
export async function consultaSinistroPT(placa: string): Promise<ConsultarSinistroPTResponse> {
  try {
    const result = await consultaSinistroPTFn({ 
      placa: placa.toUpperCase().replace(/[^A-Z0-9]/g, '')
    });
    
    return result.data as ConsultarSinistroPTResponse;
  } catch (error: unknown) {
    const err = error as { code?: string; message?: string };
    
    // Handle specific error codes
    if (err.code === 'functions/failed-precondition') {
      throw new Error('Saldo insuficiente. Por favor, compre mais créditos.');
    }
    if (err.code === 'functions/invalid-argument') {
      throw new Error(err.message || 'Dados inválidos. Verifique a placa.');
    }
    if (err.code === 'functions/unauthenticated') {
      throw new Error('Sessão expirada. Por favor, faça login novamente.');
    }
    if (err.code === 'functions/internal') {
      throw new Error(err.message || 'Erro ao consultar sinistro. Tente novamente.');
    }
    
    throw new Error('Erro ao consultar sinistro. Tente novamente.');
  }
}

